import {
  SiteProperties,
  VariantOption as HorizonOption,
} from '@thg-commerce/enterprise-aurora-edge/horizon-schema'
import { Breadcrumb } from '@thg-commerce/enterprise-aurora-landing/aurora-schema'
import {
  priceRange,
  rrpRange,
} from '@thg-commerce/enterprise-network/src/transformers/product/price'

import { Price } from '../types'
import { sortOptions } from '../utils/sortOptions'

import { ChoiceMap, FallbackChoiceMap } from './choiceMap/choiceMap'
import { Choice, TransformedChoice } from './choice'
import {
  option as optionTransformer,
  ProductChoiceTypeMap,
  TransformedOption,
} from './option'
import { ProductOptionsMap, UNSELECTED_KEY } from './productOptionsMap'
import { variant as variantTransformer } from './variant'

interface Variant {
  sku: string
  title: string
  choices: Choice[]
  inStock: boolean
  breadcrumbs?: Breadcrumb[] | null
  product?: {
    sku: number
    title: string
    url: string
    breadcrumbs?: Breadcrumb[] | null
  } | null
}

interface ChoiceToVariantMapProps {
  variants: Variant[]
  sortedHorizonOptionKeys: string[]
  choiceMap: ChoiceMap
  fallbackChoiceMap: FallbackChoiceMap
  sortedHorizonOptions: Omit<HorizonOption, 'localizedKey'>[]
  siteProperties: SiteProperties
  productChoiceTypeMap?: ProductChoiceTypeMap
  optionKeysToFilter?: string[]
  optionKeysToApplyAttribute?: string[]
  linkedOnOptionKey?: string | null
  range?: {
    priceRange?: {
      minimum: Price
      maximum: Price
    }
    rrpRange?: {
      minimum: Price
      maximum: Price
    }
  }
}

const breadcrumbTransformer = (product: {
  breadcrumbs: Breadcrumb[]
  title: string
  url: string
}) => {
  const masterProductBreadcrumbs = product.breadcrumbs
    ? product.breadcrumbs?.map((breadcrumb) => ({
        text: breadcrumb.displayName,
        url:
          breadcrumb.pagePath === '/'
            ? breadcrumb.pagePath
            : breadcrumb.pagePath.concat('.list'),
        __typename: breadcrumb.__typename,
      }))
    : []

  masterProductBreadcrumbs.push({
    text: product.title,
    url: product.url,
    __typename: 'Breadcrumb',
  })

  return masterProductBreadcrumbs
}

export const choiceToVariantMap = (props: ChoiceToVariantMapProps) =>
  props.variants.reduce<ProductOptionsMap>((choiceToVariantMap, variant) => {
    const sortedChoices = props.sortedHorizonOptionKeys.map((sortedOptionKey) =>
      variant.choices.find((choice) => choice.optionKey === sortedOptionKey),
    )

    const storedOptionKeys = new Set<string>()
    const options = props.sortedHorizonOptions.reduce<TransformedOption[]>(
      (optionAccumulator, horizonOption) => {
        if (props.optionKeysToFilter?.includes(horizonOption.key)) {
          return optionAccumulator
        }

        sortedChoices.forEach((choice) => {
          if (!choice || !props.choiceMap?.[choice.key]) {
            return
          }

          const optionKeyToChoices = Object.entries(props.choiceMap[choice.key])

          optionKeyToChoices.forEach(([optionKey, optionChoices]) => {
            if (
              optionKey !== choice.optionKey &&
              !storedOptionKeys.has(optionKey)
            ) {
              const horizonChoices = props.sortedHorizonOptions.find(
                (option) => option.key === optionKey,
              )?.choices

              let choices: TransformedChoice[] = []
              if (optionChoices.length === horizonChoices?.length) {
                choices = optionChoices
              } else {
                const choicesWithFallbacks = horizonChoices?.reduce<
                  TransformedChoice[]
                >((accumulator, horizonChoice) => {
                  const storedChoice = optionChoices.find(
                    (choice) => choice.key === horizonChoice.key,
                  )

                  const requiresFallback =
                    props.linkedOnOptionKey &&
                    optionKey === props.linkedOnOptionKey

                  if (storedChoice) {
                    accumulator.push(storedChoice)
                  } else if (
                    requiresFallback &&
                    props.fallbackChoiceMap[horizonChoice.optionKey][
                      horizonChoice.key
                    ]
                  ) {
                    accumulator.push(
                      props.fallbackChoiceMap[horizonChoice.optionKey][
                        horizonChoice.key
                      ],
                    )
                  }
                  return accumulator
                }, [])

                choices = choicesWithFallbacks ?? optionChoices
              }

              optionAccumulator.push(
                optionTransformer({
                  optionKey,
                  choices,
                  optionKeysToApplyAttribute: props.optionKeysToApplyAttribute,
                  siteProperties: props.siteProperties,
                  productChoiceTypeMap: props.productChoiceTypeMap,
                }),
              )
              storedOptionKeys.add(optionKey)
            }
          })
        })

        return optionAccumulator
      },
      [],
    )

    const sortedOptions = sortOptions({
      options,
      orderedOptionKeys: props.sortedHorizonOptionKeys,
    })

    let hashmap: {} = choiceToVariantMap
    sortedChoices.forEach((choice, index) => {
      if (!choice) {
        return
      }

      if (index < sortedChoices.length - 1) {
        if (!hashmap[choice.key]) {
          hashmap[choice.key] = {}
        }

        if (!hashmap[choice.key][UNSELECTED_KEY]) {
          const sku = variant.product?.sku || variant.sku
          const title = variant.product?.title || variant.title
          const breadcrumbs = variant.product?.breadcrumbs
            ? breadcrumbTransformer(variant.product)
            : variant.breadcrumbs

          const masterProductVariant = {
            ...variant,
            sku,
            title,
            breadcrumbs,
            priceRange: priceRange(props.range || variant.product),
            rrpRange: rrpRange(props.range || variant.product),
            notifyWhenInStockEnabled: false,
            inStock: false,
          }

          hashmap[choice.key][UNSELECTED_KEY] = {
            options: sortedOptions,
            variant: variantTransformer(masterProductVariant, false),
          }
        }
        hashmap = hashmap[choice.key]
      } else {
        if (!hashmap[choice.key]) {
          hashmap[choice.key] = {
            options: sortedOptions,
            variant: variantTransformer(variant, true),
          }
        }
      }
    })
    return choiceToVariantMap
  }, {})
